import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

export type CookieCategory =
  | "essential"
  | "functional"
  | "analytics"
  | "marketing";

export interface CookieConsent {
  essential: boolean; // Always true, cannot be disabled
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface CookieConsentContextValue {
  consent: CookieConsent | null;
  hasConsented: boolean;
  updateConsent: (newConsent: Partial<CookieConsent>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  resetConsent: () => void;
}

const CONSENT_STORAGE_KEY = "cookie_consent";
const CONSENT_VERSION = "1.0"; // Increment when cookie policy changes

const defaultConsent: CookieConsent = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(
  null,
);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [hasConsented, setHasConsented] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      console.log("🍪 Loading cookie consent from localStorage...");
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log("   Found consent:", parsed);

        // Check if consent version matches
        if (parsed.version === CONSENT_VERSION) {
          setConsent(parsed.consent);
          setHasConsented(true);
          console.log("✅ Consent loaded successfully");
          console.log("   Analytics enabled:", parsed.consent.analytics);
        } else {
          console.log("⚠️ Consent version mismatch, resetting");
          // Reset consent if version changed
          localStorage.removeItem(CONSENT_STORAGE_KEY);
        }
      } else {
        console.log("ℹ️ No cookie consent found - banner will show");
      }
    } catch (error) {
      console.error("❌ Failed to load cookie consent:", error);
    }
  }, []);

  const saveConsent = (newConsent: CookieConsent) => {
    try {
      const data = {
        consent: newConsent,
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
      };

      console.log("💾 Saving cookie consent:", data);
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(data));
      setConsent(newConsent);
      setHasConsented(true);

      // Trigger custom event for other components to react
      console.log("📢 Dispatching cookieConsentChanged event");
      window.dispatchEvent(
        new CustomEvent("cookieConsentChanged", {
          detail: newConsent,
        }),
      );
      console.log("✅ Cookie consent saved and event dispatched");
    } catch (error) {
      console.error("❌ Failed to save cookie consent:", error);
    }
  };

  const updateConsent = (newConsent: Partial<CookieConsent>) => {
    const updated: CookieConsent = {
      ...defaultConsent,
      ...consent,
      ...newConsent,
      essential: true, // Always true
    };
    saveConsent(updated);
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    });
  };

  const rejectAll = () => {
    saveConsent({
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    });
  };

  const resetConsent = () => {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    setConsent(null);
    setHasConsented(false);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasConsented,
        updateConsent,
        acceptAll,
        rejectAll,
        resetConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      "useCookieConsent must be used within CookieConsentProvider",
    );
  }
  return context;
}
